import type { WorkbenchSessionEvent } from "shared/contextProviders/workbenchSession";

type StreamEvent =
  | { type: "answer"; text: string }
  | { type: "done"; messageId: string }
  | { type: "error"; message: string };

export class WorkbenchStreamUnavailableError extends Error {}

/**
 * Sends a Workbench chat message over the SSE route and reports the answer as
 * the model writes it. Throws WorkbenchStreamUnavailableError when the stream
 * can't be opened, so callers can fall back to the GraphQL mutation.
 */
export async function streamWorkbenchMessage(input: {
  chatId: string;
  content: string;
  sessionEvents?: WorkbenchSessionEvent[];
  token: string;
  onAnswer: (answerSoFar: string) => void;
}): Promise<{ messageId: string }> {
  let response: Response;
  try {
    response = await fetch(
      `${import.meta.env.VITE_API_SERVER_URL}/workbench/chats/${input.chatId}/messages`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${input.token}`,
        },
        body: JSON.stringify({ content: input.content, sessionEvents: input.sessionEvents }),
      },
    );
  } catch (err) {
    throw new WorkbenchStreamUnavailableError(err instanceof Error ? err.message : "Network error");
  }
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => null);
    const message = body?.error ?? `Stream failed (${response.status})`;
    if (response.status >= 500 || response.status === 404) throw new WorkbenchStreamUnavailableError(message);
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
      const data = frame
        .split("\n")
        .filter(line => line.startsWith("data: "))
        .map(line => line.slice(6))
        .join("\n");
      if (!data) continue;
      let event: StreamEvent;
      try {
        event = JSON.parse(data) as StreamEvent;
      } catch {
        continue;
      }
      if (event.type === "answer") input.onAnswer(event.text);
      else if (event.type === "done") return { messageId: event.messageId };
      else if (event.type === "error") throw new Error(event.message);
    }
  }
  throw new Error("The stream ended before the reply was saved");
}
